import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  getContactImportFields,
  getContactImportJob,
  previewContactImport,
  startContactImport,
} from '../services/contact-import.service';
import type {
  ContactImportFieldsDataApi,
  ContactImportJobDataApi,
  ContactImportPreviewDataApi,
} from '../types/contact-import-api.types';

export const isActiveImportStatus = (status?: string | null) => {
  const s = (status ?? '').toLowerCase();
  return s === 'queued' || s === 'processing';
};

export const isTerminalImportStatus = (status?: string | null) => {
  const s = (status ?? '').toLowerCase();
  return s === 'completed' || s === 'failed';
};

/** Keep polling until the job reaches a terminal status — including after transient fetch errors. */
export const contactImportJobRefetchInterval = (query: {
  state: { data?: { status?: string } | null };
}): number | false => {
  if (isTerminalImportStatus(query.state.data?.status)) return false;
  return 2500;
};

export const useContactImportFields = (options: { enabled?: boolean } = {}) =>
  useGlobalQuery<ContactImportFieldsDataApi>({
    queryKey: ['contacts', 'import-fields'],
    queryFn: getContactImportFields,
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });

export const useStartContactImport = () =>
  useGlobalMutation<
    ContactImportJobDataApi,
    Error,
    {
      file: File;
      mapping: Record<string, string>;
      listIds?: string[];
      idempotencyKey: string;
    }
  >({
    mutationFn: startContactImport,
    suppressToast: true,
  });

/** The preview step's dry run. Failures fall back to the local check, so no global toast. */
export const usePreviewContactImport = () =>
  useGlobalMutation<
    ContactImportPreviewDataApi,
    Error,
    { file: File; mapping: Record<string, string>; listIds?: string[] }
  >({
    mutationFn: previewContactImport,
    suppressToast: true,
  });

export const useContactImportJob = (
  jobId: string | null,
  options: { enabled?: boolean } = {}
) => {
  const id = jobId?.trim() ?? '';
  return useGlobalQuery<ContactImportJobDataApi>({
    queryKey: ['contacts', 'import-job', id],
    queryFn: () => getContactImportJob(id),
    staleTime: 0,
    enabled: (options.enabled ?? true) && Boolean(id),
    refetchInterval: contactImportJobRefetchInterval,
  });
};
